import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dashboardService from '../common/services/next-gen/DashboardService';
import { useSelectedDashboardFacility } from './useSelectedDashboardFacility';

// Dashboard responses are scoped to the selected facility (only widgets enabled for that
// facility come back), so the cache has to be keyed by facility too - otherwise switching
// facilities would serve the previous facility's widget set. 'all' = All Facilities.
const dashboardKey = (facilityId) => ['userDashboard', facilityId || 'all'];
const widgetDefinitionsKey = (facilityId) => ['widgetDefinitions', facilityId || 'all'];

/**
 * Hook to fetch user dashboard
 */
export function useUserDashboard() {
    const { facilityId } = useSelectedDashboardFacility();

    return useQuery({
        queryKey: dashboardKey(facilityId),
        queryFn: () => dashboardService.getUserDashboard(facilityId),
        staleTime: 60000 // 1 minute
    });
}

/**
 * Hook to fetch available widget definitions
 * Only fetches user-category widgets (not system widgets)
 */
export function useWidgetDefinitions() {
    const { facilityId } = useSelectedDashboardFacility();

    return useQuery({
        queryKey: widgetDefinitionsKey(facilityId),
        queryFn: () => dashboardService.getWidgetDefinitions('user', facilityId),
        staleTime: 600000 // 10 minutes
    });
}

/**
 * Hook to update dashboard layout (drag-and-drop)
 */
export function useUpdateDashboardLayout() {
    const queryClient = useQueryClient();
    const { facilityId } = useSelectedDashboardFacility();

    return useMutation({
        mutationFn: (request) => dashboardService.updateDashboardLayout(request, facilityId),
        onSuccess: (data) => {
            queryClient.setQueryData(dashboardKey(facilityId), data);
        }
    });
}

/**
 * Hook to add a widget
 */
export function useAddWidget() {
    const queryClient = useQueryClient();
    const { facilityId } = useSelectedDashboardFacility();

    return useMutation({
        mutationFn: (request) => dashboardService.addWidget(request, facilityId),
        onSuccess: (data) => {
            queryClient.setQueryData(dashboardKey(facilityId), data);
        }
    });
}

/**
 * Hook to update a widget
 */
export function useUpdateWidget() {
    const queryClient = useQueryClient();
    const { facilityId } = useSelectedDashboardFacility();

    return useMutation({
        mutationFn: ({ userDashboardWidgetId, request }) => 
            dashboardService.updateWidget(userDashboardWidgetId, request, facilityId),
        onSuccess: (data, { userDashboardWidgetId }) => {
            queryClient.setQueryData(dashboardKey(facilityId), (old) => {
                if (!data) return old;
                if (!old?.widgets || !data?.widgets) return data;
                // The update endpoint only changes layout/title fields and never touches
                // filterConfig. Always restore the cached filterConfig for every widget
                // so a rename cannot wipe saved widget filters, regardless of what the
                // server echoes back (null, empty string, empty object, etc.).
                return {
                    ...data,
                    widgets: data.widgets.map(w => {
                        const cached = old.widgets.find(ow => ow.userDashboardWidgetId === w.userDashboardWidgetId);
                        return cached ? { ...w, filterConfig: cached.filterConfig } : w;
                    })
                };
            });
        }
    });
}

/**
 * Hook to remove a widget
 */
export function useRemoveWidget() {
    const queryClient = useQueryClient();
    const { facilityId } = useSelectedDashboardFacility();

    return useMutation({
        mutationFn: (userDashboardWidgetId) => dashboardService.removeWidget(userDashboardWidgetId, facilityId),
        onSuccess: (data) => {
            queryClient.setQueryData(dashboardKey(facilityId), data);
        }
    });
}

/**
 * Hook to save widget filters
 */
export function useSaveWidgetFilters() {
    const queryClient = useQueryClient();
    const { facilityId } = useSelectedDashboardFacility();

    return useMutation({
        mutationFn: ({ userDashboardWidgetId, filterConfig }) => 
            dashboardService.saveWidgetFilters(userDashboardWidgetId, filterConfig),
        onSuccess: (data, { userDashboardWidgetId, filterConfig }) => {
            if (data) {
                // API returned the full updated dashboard � use it directly
                queryClient.setQueryData(dashboardKey(facilityId), data);
            } else {
                // API returned nothing � patch just this widget's filterConfig in place
                queryClient.setQueryData(dashboardKey(facilityId), (old) => {
                    if (!old?.widgets) return old;
                    return {
                        ...old,
                        widgets: old.widgets.map(w =>
                            w.userDashboardWidgetId === userDashboardWidgetId
                                ? { ...w, filterConfig: JSON.stringify(filterConfig) }
                                : w
                        )
                    };
                });
            }
        }
    });
}

/**
 * Hook to toggle collapsed state
 */
export function useToggleCollapsed() {
    return useMutation({
        mutationFn: ({ userDashboardWidgetId, isCollapsed }) => 
            dashboardService.toggleCollapsed(userDashboardWidgetId, isCollapsed)
    });
}
